import React, { useCallback, useMemo } from "react"

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"
import {
  JSON_TEMPLATE_FIELDS,
  validateJsonTemplate,
} from "@/utils/validation/jsonTemplateValidation"

import { WEBSOCKET_TOOLTIPS } from "./tooltips"
import {
  AUDIO_ENCODING_OPTIONS,
  DEFAULT_AUDIO_TEMPLATE,
  DEFAULT_RECEIVE_AUDIO_CHANNELS,
  RECEIVE_AUDIO_CHANNELS_OPTIONS,
  type WebSocketFieldProps,
} from "./types"

export const AudioFormatFields: React.FC<WebSocketFieldProps> = ({
  metadata,
  onMetadataUpdate,
  disabled = false,
}) => {
  const audioTemplateError = useMemo(
    () =>
      validateJsonTemplate(
        metadata.send_audio_template,
        JSON_TEMPLATE_FIELDS.audio,
      ),
    [metadata.send_audio_template],
  )

  const handleAudioTemplateChange = useCallback(
    (value: string) => {
      onMetadataUpdate({ send_audio_template: value })
    },
    [onMetadataUpdate],
  )

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-coval-text">Audio Format</h4>

      <div className="space-y-4">
        <h5 className="text-xs font-semibold text-coval-text-muted">
          Inbound (sent to your agent)
        </h5>

        <div className="space-y-2">
          <FieldLabel tooltip={WEBSOCKET_TOOLTIPS.sendAudioTemplate}>
            Send Audio Template
          </FieldLabel>
          <JsonCodeEditor
            hideGutters
            copyable
            ariaLabel="Send Audio Template"
            placeholder={DEFAULT_AUDIO_TEMPLATE}
            value={metadata.send_audio_template || ""}
            onChange={handleAudioTemplateChange}
            rows={4}
            disabled={disabled}
            error={audioTemplateError}
            // Raw binary mode uses the bare `{{audio_data}}` template.
            lint={false}
          />
        </div>
      </div>

      <div className="space-y-4">
        <h5 className="text-xs font-semibold text-coval-text-muted">
          Outbound (received from your agent)
        </h5>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="space-y-2">
            <FieldLabel
              htmlFor="message-type-path"
              tooltip={WEBSOCKET_TOOLTIPS.messageTypePath}
            >
              Message Type Path
            </FieldLabel>
            <Input
              id="message-type-path"
              aria-label="Message Type Path"
              value={metadata.message_type_path || ""}
              placeholder="Enter the message type path"
              onChange={(e) =>
                onMetadataUpdate({ message_type_path: e.target.value })
              }
              disabled={disabled}
            />
          </div>

          <div className="space-y-2">
            <FieldLabel
              htmlFor="audio-message-type"
              tooltip={WEBSOCKET_TOOLTIPS.audioMessageType}
            >
              Audio Message Type
            </FieldLabel>
            <Input
              id="audio-message-type"
              aria-label="Audio Message Type"
              value={metadata.audio_message_type_value || ""}
              placeholder="Enter the audio message type"
              onChange={(e) =>
                onMetadataUpdate({
                  audio_message_type_value: e.target.value,
                })
              }
              disabled={disabled}
            />
          </div>

          <div className="space-y-2">
            <FieldLabel
              htmlFor="audio-data-path"
              tooltip={WEBSOCKET_TOOLTIPS.audioDataPath}
            >
              Audio Data Path
            </FieldLabel>
            <Input
              id="audio-data-path"
              aria-label="Audio Data Path"
              value={metadata.audio_data_path || ""}
              placeholder="Enter the audio data path"
              onChange={(e) =>
                onMetadataUpdate({ audio_data_path: e.target.value })
              }
              disabled={disabled}
            />
          </div>

          <div className="space-y-2">
            <FieldLabel
              htmlFor="audio-encoding"
              tooltip={WEBSOCKET_TOOLTIPS.audioEncoding}
            >
              Audio Encoding
            </FieldLabel>
            <Select
              value={metadata.audio_encoding || "pcm"}
              onValueChange={(value) =>
                onMetadataUpdate({ audio_encoding: value })
              }
              disabled={disabled}
            >
              <SelectTrigger id="audio-encoding" aria-label="Audio Encoding">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUDIO_ENCODING_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <FieldLabel
              htmlFor="receive-audio-channels"
              tooltip={WEBSOCKET_TOOLTIPS.receiveAudioChannels}
            >
              Channels
            </FieldLabel>
            <Select
              value={String(
                metadata.receive_audio_channels ??
                  DEFAULT_RECEIVE_AUDIO_CHANNELS,
              )}
              onValueChange={(value) =>
                onMetadataUpdate({ receive_audio_channels: Number(value) })
              }
              disabled={disabled}
            >
              <SelectTrigger id="receive-audio-channels" aria-label="Channels">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECEIVE_AUDIO_CHANNELS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  )
}
